import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Feature } from 'geojson';
import { LocationMessage } from '../../../models/vessels/internal-ais-api-v3.model';
import {
  defaultValues,
  type NearbyVesselsFormValues,
} from './nearby-vessels-form/nearby-vessels-form-values';

interface NearbyVesselsState {
  nearbyVessels: LocationMessage[] | null;
  nearbyVesselsFormValues: NearbyVesselsFormValues;
  nearbyVesselsPoints: Feature<GeoJSON.Point, GeoJSON.GeoJsonProperties>[];
  loading: boolean;
  error: boolean;
  vesselTypeDefaultColouring: boolean;
  token: string | undefined;
}

const initialState: NearbyVesselsState = {
  nearbyVessels: null,
  nearbyVesselsFormValues: defaultValues,
  nearbyVesselsPoints: [],
  loading: false,
  error: false,
  vesselTypeDefaultColouring: false,
  token: undefined,
};

const nearbyVesselsSlice = createSlice({
  name: 'nearbyVessels',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setNearbyVessels: (
      state,
      action: PayloadAction<LocationMessage[] | null>
    ) => {
      state.nearbyVessels = action.payload;
      state.loading = false;
      state.error = false;
    },
    setNearbyVesselsFormValues: (
      state,
      action: PayloadAction<Partial<NearbyVesselsFormValues>>
    ) => {
      state.nearbyVesselsFormValues = {
        ...state.nearbyVesselsFormValues,
        ...action.payload,
      };
    },
    appendNearbyVessels: (state, action: PayloadAction<LocationMessage[]>) => {
      const newData = action.payload;
      if (newData) {
        // Retain all data points — deduplication happens at render/display time
        // so that the full dataset is available for future playback/scrubbing.
        state.nearbyVessels = [...(state.nearbyVessels || []), ...newData];
      }
    },
    setError: (state, action: PayloadAction<boolean>) => {
      state.error = action.payload;
    },
    setVesselTypeDefaultColouring: (state, action: PayloadAction<boolean>) => {
      state.vesselTypeDefaultColouring = action.payload;
    },
    setToken: (state, action: PayloadAction<string | undefined>) => {
      state.token = action.payload;
    },
  },
});

export const {
  setLoading,
  setNearbyVessels,
  appendNearbyVessels,
  setNearbyVesselsFormValues,
  setError,
  setVesselTypeDefaultColouring,
  setToken,
} = nearbyVesselsSlice.actions;

export default nearbyVesselsSlice.reducer;
